import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { useLoyaltyStore } from '../../hooks';
import { getLoyaltyIconByMachineType } from '../../loyalty.constants';
import { ArrowRightIcon } from '../icons';

import styles from './LoyaltyProgress.module.css';

/** Above this many points the single row stops being readable: at 10 the track
 *  is 12 columns wide inside a 375px card, so each dot is ~20px and the icon
 *  inside it ~10px. Past the threshold the dots move into two rows, which is
 *  what keeps the card from growing taller (Vadym, 25.08). */
const TWO_ROW_THRESHOLD = 8;

/** Equal columns that may shrink below the dot's content size — the dots fill
 *  the row the same way they do in the single-row track, and the CSS caps how
 *  big one dot may get. See the comment above `.progressTrackTwoRows`. */
const TWO_ROW_DOT_COLUMN = 'minmax(0, 1fr)';

/** Splitting the points down the middle makes a 12-point row only six columns
 *  wide, so each dot swells to 34px and the card grows to 159px. Filling the
 *  first row up to this many columns before wrapping keeps the dot at 26px and
 *  the card at 144px — the same height a 16-point card already has. Below ~26px
 *  the machine icon inside the dot stops reading, which is why this is 8 and not
 *  more. */
const TWO_ROW_MAX_PER_ROW = 8;

export const LoyaltyProgress: React.FC = () => {
    const machineTypeId = useAppStore((state) => state.vm.type_id);
    const loyaltyId = useLoyaltyStore((state) => state.loyalty_id);
    const currentBalance = useLoyaltyStore((state) => state.currentBalance);
    const balanceLimit = useLoyaltyStore(
        (state) => state.pointsToShowLoyaltyBonus,
    );
    const isPrizeAvailable = useLoyaltyStore((state) =>
        state.isPrizeAvailable(),
    );

    const { t } = useTranslation();

    const BalanceItemIcon = getLoyaltyIconByMachineType(machineTypeId);
    const isTwoRows = balanceLimit > TWO_ROW_THRESHOLD;
    const dotsPerRow = isTwoRows
        ? Math.max(Math.ceil(balanceLimit / 2), TWO_ROW_MAX_PER_ROW)
        : balanceLimit;

    if (!loyaltyId) {
        return null;
    }

    const dots = [...Array(balanceLimit)].map((_, index) => {
        const isFilled = index < currentBalance;

        const balanceItemClass = isFilled
            ? styles.balanceItemFilled
            : styles.balanceItemEmptyThemed;

        return (
            <div
                key={index}
                className={`${styles.balanceItem} ${balanceItemClass}`}
            >
                <BalanceItemIcon
                    className={styles.balanceItemIcon}
                    size="small"
                />
            </div>
        );
    });

    const reward = (
        <div
            className={`${styles.rewardIcon} ${
                isPrizeAvailable
                    ? styles.rewardReady
                    : styles.rewardLockedThemed
            }`}
        >
            <BalanceItemIcon />
        </div>
    );

    return (
        <div className={`${styles.card} ${styles.cardThemed}`}>
            {/* Header text */}
            <div className={styles.header}>
                <p className={styles.subtitle}>
                    {isPrizeAvailable
                        ? t('loyalty.progress.headerPrizeReady')
                        : t('loyalty.progress.headerDefault')}
                </p>
                <p className={styles.bodyText}>
                    {isPrizeAvailable
                        ? t('loyalty.progress.descriptionPrizeReady')
                        : t('loyalty.progress.descriptionDefault', {
                              count: balanceLimit - currentBalance,
                          })}
                </p>
            </div>

            {/* Progress Track. Two rows put the dots in their own grid and push
                the arrow and the reward to the right edge with a spacer; one row
                lays everything out in a single stretched grid. */}
            {isTwoRows ? (
                <div
                    className={`${styles.progressTrack} ${styles.progressTrackTwoRows}`}
                >
                    <div
                        className={styles.dotsGroup}
                        style={{
                            gridTemplateColumns: `repeat(${dotsPerRow}, ${TWO_ROW_DOT_COLUMN})`,
                        }}
                    >
                        {dots}
                    </div>
                    <ArrowRightIcon className={styles.arrow} />
                    {reward}
                </div>
            ) : (
                <div
                    className={styles.progressTrack}
                    style={{
                        gridTemplateColumns: `repeat(${dotsPerRow}, 1fr) 0.875fr 1.125fr`,
                    }}
                >
                    {dots}
                    <ArrowRightIcon className={styles.arrow} />
                    {reward}
                </div>
            )}
        </div>
    );
};
